import Random from "rung/src/random";
import Glyphs from "../glyphs";
import Tiles from "../tiles";
import Entity from "./entity";
import Mass from "./mass";
import Speed from "./speed";
import Health from "./health";
import Statblock from "./statblock";
import Description from "./description";
import * as behaviour from "./behaviour";

const rng = new Random(Math.random);

class MonstrousAnimalSense {
  constructor() {
    this.chasing = false;
  }

  isEnemy() {
    return true;
  }

  shotFrom() {
    this.chasing = true;
  }

  bumpFrom(entity) {
    return true;
  }

  takeTurn(stage) {
    if (this.chasing) {
      behaviour.meleeCharge(this.owner, stage.player, stage);
    } else {
      if (stage.isVisible(this.owner.x, this.owner.y)) {
        this.chasing = true;
        // Spend a turn here...
      } else {
        behaviour.drunkenWalk(this.owner, stage);
      }
    }
  }
}

class WildAnimalSense {
  constructor() {
    this.chasing = false;
  }

  isEnemy() {
    return this.chasing;
  }

  shotFrom(entity) {
    this.chasing = true;
  }

  bumpFrom(entity) {
    if (entity.isPlayer()) {
      this.chasing = true;
      return true;
    }
    return false;
  }

  takeTurn(stage) {
    if (this.chasing) {
      behaviour.meleeCharge(this.owner, stage.player, stage);
    } else {
      behaviour.drunkenWalk(this.owner, stage);
    }
  }
}

class FarmAnimalSense {
  constructor(home) {
    this.home = home;
    this.cooldown = 5;
    this.grazed = new Set();
    this.frightened = false;
  }

  isEnemy() {
    return false;
  }

  shotFrom(entity) {
    this.frightened = true;
    this.owner.speed.quadruple();
  }

  bumpFrom(entity) {
    if (entity.isPlayer()) {
      this.frightened = true;
      this.owner.speed.quadruple();
    }
  }

  resetCooldown() {
    this.cooldown = 5;
  }

  takeTurn(stage) {
    if (this.frightened) {
      const adjacent = stage.adjacentPoints(this.owner.x, this.owner.y);
      const closeToSafety = adjacent.some(p => p.x == this.home.x && p.y == this.home.y);
      if (closeToSafety) {
        if (this.cooldown == 0) {
          this.resetCooldown();
          this.owner.speed.quarter();
          this.frightened = false;
        } else {
          this.cooldown--;
        }
      } else {
        behaviour.meleeCharge(this.owner, this.home, stage);
      }
    } else if (stage.isOf(this.owner.x, this.owner.y, Tiles.Cabbages)) {
      if (this.cooldown == 0) {
        this.resetCooldown();
        this.grazed.add(`${this.owner.x}:${this.owner.y}`);
        behaviour.drunkenWalk(this.owner, stage);
      } else {
        this.cooldown--;
      }
    } else if (stage.isAdjacentTo(this.owner.x, this.owner.y, Tiles.Cabbages)) {
      const food = stage.adjacentTilesOf(this.owner.x, this.owner.y, Tiles.Cabbages);
      // Very shaky but does the job
      if (rng.boolean()) food.reverse();
      const target = food.find(t => this.grazed)

      if (target) {
        behaviour.walkTo(this.owner, target, stage);
      } else {
        behaviour.drunkenWalk(this.owner, stage);
      }
    } else {
      behaviour.drunkenWalk(this.owner, stage);
    }
  }
}

class NaturalAttack {
  constructor(name, damage) {
    this.name = name;
    this.damage = damage;
  }
}

class Animals {
  static sheep(home) {
    return new Entity(null, null, Glyphs.Sheep, "Sheep", {
      mass: new Mass(Mass.Medium),
      speed: new Speed(5),
      health: new Health(8),
      sense: new FarmAnimalSense(home)
    });
  }

  static direFox() {
    return new Entity(null, null, Glyphs.Fox, "Dire Fox", {
      mass: new Mass(Mass.Medium),
      speed: new Speed(15),
      health: new Health(8),
      stats: new Statblock(8, 15, 10),
      sense: new WildAnimalSense(),
      naturalAttack: new NaturalAttack("bite", 5)
    });
  }

  static direRat() {
    return new Entity(null, null, Glyphs.Rat, "Dire Rat", {
      mass: new Mass(Mass.Small),
      speed: new Speed(12),
      health: new Health(6),
      stats: new Statblock(8, 12, 10),
      sense: new MonstrousAnimalSense(),
      naturalAttack: new NaturalAttack("bite", 4)
    });
  }

  static moleRat() {
    return new Entity(null, null, Glyphs.MoleRat, "Mole Rat", {
      mass: new Mass(Mass.Small),
      speed: new Speed(8),
      health: new Health(4),
      stats: new Statblock(8, 8, 10),
      sense: new MonstrousAnimalSense(),
      naturalAttack: new NaturalAttack("bite", 3)
    });
  }

  static vampireBat() {
    return new Entity(null, null, Glyphs.Bat, "Vampire Bat", {
      mass: new Mass(Mass.Small),
      speed: new Speed(14),
      health: new Health(4),
      stats: new Statblock(6, 16, 6),
      sense: new MonstrousAnimalSense(),
      naturalAttack: new NaturalAttack("bite", 7)
    });
  }

  static caveSlug() {
    return new Entity(null, null, Glyphs.CaveSlug, "Cave Slug", {
      mass: new Mass(Mass.Small),
      speed: new Speed(7),
      health: new Health(4),
      stats: new Statblock(8, 6, 12),
      sense: new MonstrousAnimalSense(),
      naturalAttack: new NaturalAttack("acid burn", 3)
    });
  }

  static caveSpider() {
    const glyph = rng.boolean() ? Glyphs.CaveSpider1 : Glyphs.CaveSpider2;
    return new Entity(null, null, glyph, "Cave Spider", {
      mass: new Mass(Mass.Small),
      speed: new Speed(9),
      health: new Health(9),
      stats: new Statblock(6, 12, 6),
      sense: new MonstrousAnimalSense(),
      naturalAttack: new NaturalAttack("bite", 3)
    });
  }

  static giantSpider() {
    const glyph = rng.boolean() ? Glyphs.GiantSpider1 : Glyphs.GiantSpider2;
    return new Entity(null, null, glyph, "Giant Spider", {
      mass: new Mass(Mass.Large),
      speed: new Speed(7),
      health: new Health(38),
      stats: new Statblock(14, 12, 8),
      sense: new MonstrousAnimalSense(),
      naturalAttack: new NaturalAttack("bite", 8)
    });
  }
}

export default Animals;
